/**
 * Meals API service
 */

import { apiClient } from './client';
import type {
  Meal,
  CreateMealRequest,
  UpdateMealRequest,
  AddTagRequest,
  ListMealsQuery,
  ListMealsResponse,
  GetAllTagsResponse,
} from '@/types/api';

/**
 * GET /api/meals
 * List meals with pagination and filters
 */
export async function getMeals(params?: ListMealsQuery): Promise<ListMealsResponse> {
  const response = await apiClient.get<ListMealsResponse>('/meals', {
    params: {
      ...params,
      tags: params?.tags?.join(','), // Convert array to comma-separated string
    },
  });
  return response.data;
}

/**
 * GET /api/meals/:id
 * Get meal by ID
 */
export async function getMealById(id: number): Promise<Meal> {
  const response = await apiClient.get<Meal>(`/meals/${id}`);
  return response.data;
}

/**
 * POST /api/meals
 * Create a new meal
 */
export async function createMeal(data: CreateMealRequest): Promise<Meal> {
  const response = await apiClient.post<Meal>('/meals', data);
  return response.data;
}

/**
 * PATCH /api/meals/:id
 * Update meal
 */
export async function updateMeal(id: number, data: UpdateMealRequest): Promise<Meal> {
  const response = await apiClient.patch<Meal>(`/meals/${id}`, data);
  return response.data;
}

/**
 * DELETE /api/meals/:id
 * Delete meal
 */
export async function deleteMeal(id: number): Promise<void> {
  await apiClient.delete(`/meals/${id}`);
}

/**
 * POST /api/meals/:id/tags
 * Add tag to meal
 */
export async function addTagToMeal(id: number, data: AddTagRequest): Promise<Meal> {
  const response = await apiClient.post<Meal>(`/meals/${id}/tags`, data);
  return response.data;
}

/**
 * DELETE /api/meals/:id/tags/:tagId
 * Remove tag from meal
 */
export async function removeTagFromMeal(mealId: number, tagId: number): Promise<void> {
  await apiClient.delete(`/meals/${mealId}/tags/${tagId}`);
}

/**
 * GET /api/meals/tags
 * Get all unique tags with counts
 */
export async function getAllTags(): Promise<GetAllTagsResponse> {
  const response = await apiClient.get<GetAllTagsResponse>('/meals/tags');
  return response.data;
}
